import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Button, Input, Spinner } from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import AwesomePagination from "../../Components/Common/AwesomePagination";
import { toast } from "react-toastify";
import ModalParceiro from "./ModalParceiro";
import ModalConfirmacao from "./ModalConfirmacao";
import SidebarParceiro from "./SidebarParceiro";

const apiClient = new APIClient();

const TabParceiros = () => {
  const [loading, setLoading] = useState(false);
  const [parceiros, setParceiros] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [busca, setBusca] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [parceiroAtual, setParceiroAtual] = useState(null);
  const [modalConfirmacaoOpen, setModalConfirmacaoOpen] = useState(false);
  const [parceiroParaRemover, setParceiroParaRemover] = useState(null);

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleModalConfirmacao = () => setModalConfirmacaoOpen(!modalConfirmacaoOpen);

  const fetchParceiros = useCallback(async (currentPage, searchTerm) => {
    setLoading(true);
    const params = {
      itens_pagina: 20,
      pagina: currentPage,
      termo: searchTerm,
    };

    try {
      const response = await apiClient.post(`/parceiros/busca`, params);
      if (response?.dados) {
        const dados = response.dados;
        setParceiros(dados.data);
        setTotalPaginas(dados.last_page);
      } else {
        toast.error(response.mensagem || "Erro ao buscar parceiros");
      }
    } catch (error) {
      console.error("Erro completo:", error);
      toast.error("Erro ao buscar parceiros");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await fetchParceiros(pagina, busca);
    };
    fetchData();
  }, [pagina, busca, fetchParceiros]);

  const handleEditarParceiro = (parceiro) => {
    setParceiroAtual(parceiro);
    setSidebarOpen(true);
  };

  const handleRemoverParceiro = (parceiro) => {
    setParceiroParaRemover(parceiro);
    setModalConfirmacaoOpen(true);
  };

  const confirmarRemocao = async () => {
    if (!parceiroParaRemover) return;

    setLoading(true);
    try {
      const response = await apiClient.delete(`/parceiros/${parceiroParaRemover.id}`);
      if (response?.mensagem) {
        toast.success("Parceiro removido com sucesso!");
        await fetchParceiros(pagina, busca);
      } else {
        toast.error(response?.mensagem || "Erro ao remover parceiro");
      }
    } catch (error) {
      toast.error("Erro ao remover parceiro");
    }
    setLoading(false);
    setModalConfirmacaoOpen(false);
    setParceiroParaRemover(null);
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Parceiros</h4>
            {parceiros && parceiros.length > 0 && (
              <Button
                color="info"
                className="btn-info text-white fw-semibold"
                onClick={toggleModal}
              >
                Cadastrar Parceiro
              </Button>
            )}
          </div>

          {!parceiros || (parceiros.length === 0 && !loading) ? (
            <div className="text-center py-5">
              <div className="text-muted mb-3">
                Você ainda não tem parceiros cadastrados.
              </div>
              <Button 
                color="info" 
                className="btn-info text-white fw-semibold"
                onClick={toggleModal}
              >
                Cadastrar Parceiro
              </Button>
            </div>
          ) : (
            <>
              <div className="d-flex gap-2 mb-3">
                <Input
                  type="search"
                  placeholder="Buscar parceiros..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="w-25"
                />
                <Button
                  color="light"
                  className="fw-semibold"
                  onClick={() => fetchParceiros(1, busca)}
                >
                  Buscar
                </Button>
              </div>

              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="info" />
                  </div>
                ) : (
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Nome</th>
                        <th scope="col">Contato</th>
                        <th scope="col">Redes Sociais</th>
                        <th scope="col">Resgates</th>
                        <th scope="col" style={{ width: "150px" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {parceiros.map((parceiro) => (
                        <tr key={parceiro.id}>
                          <td>
                            <div className="fw-medium">{parceiro.nome}</div>
                            <div className="text-muted small">
                              Desde {new Date(parceiro.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td>
                            <div>{parceiro.email}</div>
                            {parceiro.tel && (
                              <div className="text-muted small">{parceiro.tel}</div>
                            )}
                          </td>
                          <td>
                            {parceiro.redes_sociais?.length > 0 ? (
                              <div className="d-flex gap-2">
                                {parceiro.redes_sociais.map((rede) => (
                                  <a
                                    key={rede.id}
                                    href={rede.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-muted"
                                  >
                                    <i className={`ri-${rede.rede}-line`}></i>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted small">Nenhuma rede social</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="fw-medium">{parceiro.total_resgatados || 0}</div>
                              {parceiro.total_resgatados > 0 && (
                                <div className="ms-2">
                                  <i className="ri-coupon-line text-warning"></i>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button 
                                color="light" 
                                size="sm" 
                                className="btn-icon"
                                onClick={() => handleEditarParceiro(parceiro)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                              <Button 
                                color="danger" 
                                size="sm" 
                                className="btn-icon"
                                onClick={() => handleRemoverParceiro(parceiro)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <AwesomePagination
                pagina={pagina}
                totalPaginas={totalPaginas}
                setPagina={setPagina}
              />
            </>
          )}
        </CardBody>
      </Card>

      <ModalParceiro
        isOpen={modalOpen}
        toggle={toggleModal}
        onSuccess={() => fetchParceiros(pagina, busca)}
      />

      <SidebarParceiro
        isOpen={sidebarOpen}
        toggle={toggleSidebar}
        parceiro={parceiroAtual}
        onSuccess={() => {
          fetchParceiros(pagina, busca);
          setSidebarOpen(false);
        }}
      />

      <ModalConfirmacao
        isOpen={modalConfirmacaoOpen}
        toggle={toggleModalConfirmacao}
        onConfirm={confirmarRemocao}
        title="Remover Parceiro"
        message="Tem certeza que deseja remover este parceiro? Esta ação não poderá ser desfeita."
      />
    </>
  );
};

export default TabParceiros; 